import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeProps } from 'reactflow';
import { store } from 'src/redux/store';
import { setNodeConfiguration } from 'src/redux/workflows/slice';
import {
  formatConditionDisplayValue,
  formatConditionValue,
  getOperatorTranslation,
  mapFieldTypeToValueType,
} from 'src/utils/general';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { LIST_ROUTE_BY_MODEL, recordListDomain } from 'src/utils/workflowRoutes';
import { useConditionPreview, WorkflowConditionPreview } from 'src/services/workflows';
import WorkflowNodeCard from '../WorkflowNodeCard/WorkflowNodeCard';
import cardStyles from '../WorkflowNodeCard/WorkflowNodeCard.module.scss';
import ConditionSelectionOverlay, {
  ConditionFormData,
  ConditionGroupFormData,
} from './ConditionSelectionOverlay/ConditionSelectionOverlay';

const ConditionNode: React.FC<NodeProps> = ({ id, data }) => {
  const { t } = useTranslation();
  const conditionOperatorLabel = (c: ConditionFormData) => {
    if (!c.value && c.condition === '!=') return t('workflows.condition_is_set');
    if (!c.value && c.condition === '=') return t('workflows.condition_is_empty');
    return getOperatorTranslation(c.condition);
  };

  const [groupData, setGroupData] = useState<ConditionGroupFormData | null>(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [preview, setPreview] = useState<WorkflowConditionPreview | null>(null);
  const triggerModel = useSelector(getWorkflows).selectedTrigger?.model;
  const navigate = useNavigate();
  const listRoute = triggerModel ? LIST_ROUTE_BY_MODEL[triggerModel] : undefined;
  const previewMutation = useConditionPreview();
  const { mutateAsync: requestPreview } = previewMutation;

  React.useEffect(() => {
    const complete = groupData?.conditions.filter(c => c.attribute && c.condition) ?? [];
    if (!triggerModel || !complete.length) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await requestPreview({
          body: {
            model: triggerModel,
            group: {
              operator: groupData?.operator || 'and',
              condition_ids: complete.map(c => ({
                attribute: c.attribute,
                condition: formatConditionValue(c.condition),
                value: c.value,
                value_type: mapFieldTypeToValueType(c.valueType, c.value),
              })) as any,
              subgroup_ids: [],
            },
          },
        });
        if (!cancelled) setPreview(result);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [groupData, triggerModel, requestPreview]);

  // Restore from existing data (e.g. when viewing an existing workflow)
  React.useEffect(() => {
    if (data.conditionData && !groupData) {
      // Handle legacy single-condition format from GET response
      if (data.conditionData.attribute) {
        const { attribute, condition, value } = data.conditionData;
        const singleCondition: ConditionFormData = {
          attribute,
          attributeLabel: attribute,
          condition,
          conditionLabel: condition,
          value: value || '',
          valueLabel: value || '',
          valueType: 'string',
        };

        const restored: ConditionGroupFormData = {
          operator: 'and',
          conditions: [singleCondition],
        };
        setGroupData(restored);

        store.dispatch(
          setNodeConfiguration({
            nodeId: id,
            nodeType: 'condition',
            data: {
              operator: 'and',
              conditions: [
                {
                  attribute,
                  condition,
                  value: value || '',
                  valueType: 'string',
                },
              ],
              subgroups: [],
            },
          }),
        );
      }
      // Handle condition group format
      else if (data.conditionData.conditions) {
        const { operator, conditions } = data.conditionData;
        const restoredConditions: ConditionFormData[] = conditions.map((c: any) => ({
          attribute: c.attribute,
          attributeLabel: c.attributeLabel || c.attribute,
          condition: c.condition,
          conditionLabel: c.conditionLabel || c.condition,
          value: c.value || '',
          valueLabel: c.valueLabel || c.value || '',
          valueType: c.valueType || 'string',
        }));

        const restored: ConditionGroupFormData = {
          operator: operator || 'and',
          conditions: restoredConditions,
        };
        setGroupData(restored);

        store.dispatch(
          setNodeConfiguration({
            nodeId: id,
            nodeType: 'condition',
            data: {
              operator: operator || 'and',
              conditions: restoredConditions.map(c => ({
                attribute: c.attribute,
                condition: c.condition,
                value: c.value,
                valueLabel: c.valueLabel,
                valueType: c.valueType,
              })),
              subgroups: [],
            },
          }),
        );
      }
    }
  }, [data.conditionData]);

  const handleConditionSelect = (formData: ConditionGroupFormData) => {
    if (!data.isEditMode) return;

    setGroupData(formData);

    store.dispatch(
      setNodeConfiguration({
        nodeId: id,
        nodeType: 'condition',
        data: {
          operator: formData.operator,
          conditions: formData.conditions.map(c => ({
            attribute: c.attribute,
            condition: c.condition,
            value: c.value,
            valueType: c.valueType,
          })),
          subgroups: [],
        },
      }),
    );
  };

  const handleResetClick = () => {
    if (data.isEditMode && data.removeNode) {
      data.removeNode(id);
    } else if (data.isEditMode) {
      setGroupData(null);
      if (data.removeLastNode) {
        data.removeLastNode();
        data.removeLastNode();
      }
    }
  };

  const handleCardClick = () => {
    setIsOverlayOpen(true);
  };

  const getConditionSummary = () => {
    if (!groupData || groupData.conditions.length === 0) return undefined;

    const summarize = (c: ConditionFormData) => {
      const attrLabel = t(`general.${c.attribute}`, { defaultValue: c.attributeLabel || c.attribute });
      const condLabel = conditionOperatorLabel(c);
      const valLabel = formatConditionDisplayValue(c.valueLabel || c.value);
      const parts = [attrLabel, condLabel];
      if (valLabel) parts.push(valLabel);
      return parts.join(' ');
    };

    if (groupData.conditions.length === 1) {
      return summarize(groupData.conditions[0]);
    }

    const operatorLabel = groupData.operator.toUpperCase();
    return groupData.conditions.map(summarize).join(` ${operatorLabel} `);
  };

  const conditionChips = () => {
    if (!groupData || groupData.conditions.length === 0) return undefined;
    return (
      <div className={cardStyles.chips}>
        {groupData.conditions.map((c, index) => (
          <span key={`${c.attribute}-${index}`} className={cardStyles.chip}>
            <strong>{t(`general.${c.attribute}`, { defaultValue: c.attributeLabel || c.attribute })}</strong>{' '}
            {conditionOperatorLabel(c)} <strong>{formatConditionDisplayValue(c.valueLabel || c.value)}</strong>
          </span>
        ))}
      </div>
    );
  };

  const conditionTitle = () => {
    if (!groupData || groupData.conditions.length === 0) return undefined;
    if (groupData.conditions.length === 1) return getConditionSummary();
    return groupData.operator === 'or' ? t('workflows.conditions_any') : t('workflows.conditions_all');
  };

  return (
    <WorkflowNodeCard
      id={id}
      label={t('workflows.node_label_condition')}
      title={t('workflows.condition_overlay.check_if')}
      cardText={t('workflows.condition_overlay.title')}
      isEditMode={data.isEditMode}
      isSelected={!!groupData}
      selectedTitle={conditionTitle()}
      selectedSubtitle={t('workflows.logic')}
      selectedIcon="signPost"
      summary={
        groupData && (groupData.conditions.length > 1 || preview) ? (
          <>
            {groupData.conditions.length > 1 && conditionChips()}
            {preview && (
              <span className="typo-text-12 text-faded">
                {t(preview.capped ? 'workflows.preview.matches_capped' : 'workflows.preview.matches', {
                  matched: preview.matched,
                  evaluated: preview.evaluated,
                })}
                {listRoute && !!preview.record_ids?.length && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="typo-text-12--bold text-full underline"
                      onClick={event => {
                        event.stopPropagation();
                        navigate(listRoute, {
                          state: { preselectedDomain: recordListDomain(preview.record_ids ?? []) },
                        });
                      }}>
                      {t('workflows.preview.show')}
                    </button>
                  </>
                )}
              </span>
            )}
          </>
        ) : undefined
      }
      onCardClick={handleCardClick}
      onResetClick={handleResetClick}>
      <ConditionSelectionOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        onConditionSelect={handleConditionSelect}
        initialData={groupData}
      />
    </WorkflowNodeCard>
  );
};

export default ConditionNode;
